<template>
    <v-card class="mt-4">
        <v-card-title>
            <v-layout row wrap>
                <v-col grow class="ma-2"> Task Templates </v-col>
                <v-btn color="primary" class="ma-2" @click="showCreateSchemaConfigTaskTemplateForm">
                    New Task Template
                </v-btn>
            </v-layout>
        </v-card-title>
        <v-data-table
            :headers="schemaConfigTaskTemplateHeaders"
            :items="schemaConfigTaskTemplateListItems"
            no-results-text="No tasks."
            item-key="id"
            class="elevation-1"
            :items-per-page="25"
        >
            <template v-slot:item.edit="{ item }">
                <v-icon @click="showEditSchemaConfigTaskTemplateForm(item)">$pencil</v-icon>
            </template>
            <template v-slot:item.delete="{ item }">
                <v-icon @click="deleteSchemaConfigTaskTemplate(item.schemaConfigTaskTemplate)"
                >$delete</v-icon
                >
            </template>
        </v-data-table>
        <v-dialog v-model="showSchemaConfigTaskTemplateFormDialog" width="1000" persistent>
            <schema-config-task-template-form
                v-if="showSchemaConfigTaskTemplateFormDialog"
                :readout-groups="readoutGroups"
                :task-groups="taskGroups"
                :schema-config-task-template="selectedSchemaConfigTaskTemplate ?? null"
                :schemaConfig="schemaConfig"
                @closed="schemaConfigTaskTemplateFormDialogClosed"
                @saved="savedSchemaConfigTaskTemplate"
            />
        </v-dialog>
    </v-card>
</template>
<script setup lang="ts">
import { ref, computed } from "vue";
import { SavedSchemaConfigDTO } from "@/services/schemas/manageSchemasAPI";
import { SavedTaskGroup } from "@/services/taskGroups/taskGroupsAPI";
import { SavedReadoutGroup } from "@/services/readoutGroups/readoutGroupsAPI";
import {
    SavedSchemaConfigTaskTemplate,
    taskTemplatesAPI,
} from "@/services/taskTemplates/taskTemplatesAPI";
import { findReadoutGroupById, findTaskGroupById } from "@/lib/taskUtils";
import _ from "lodash";
import { withLoadingScreen } from "@/lib/loading";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import SchemaConfigTaskTemplateForm from "@/components/SchemaConfig/SchemaConfigTaskTemplateForm.vue";

const { schemaConfig, schemaConfigTaskTemplates, taskGroups, readoutGroups } = defineProps<{
    schemaConfig: SavedSchemaConfigDTO,
    schemaConfigTaskTemplates: SavedSchemaConfigTaskTemplate[],
    taskGroups: SavedTaskGroup[],
    readoutGroups: SavedReadoutGroup[],
}>();

const emit = defineEmits<{
    (e: "saved");
    (e: "deleted");
}>()

export type SchemaConfigTaskTemplateListItem = SavedSchemaConfigTaskTemplate & {
    schemaConfigTaskTemplate: SavedSchemaConfigTaskTemplate,
    taskGroupName: string,
    readoutGroupName: string,
}

const showSchemaConfigTaskTemplateFormDialog = ref<boolean>(false);
const selectedSchemaConfigTaskTemplate = ref<SavedSchemaConfigTaskTemplate>();
const schemaConfigTaskTemplateListItems = computed(() => {
    return mapSchemaConfigTaskTemplatesToListItems(schemaConfigTaskTemplates);
})

const schemaConfigTaskTemplateHeaders = ref<VDataTableHeader[]>([
    {
        title: "Day",
        value: "day",
        align: "start",
        sortable: true,
        width: "5%",
    },
    {
        title: "Task Group",
        value: "taskGroupName",
        sortable: true,
        align: "start",
        width: "10%",
    },
    {
        title: "Readout Group",
        value: "readoutGroupName",
        sortable: true,
        align: "start",
        width: "12%",
    },
    {
        title: "Edit",
        value: "edit",
        align: "start",
        width: "5%",
    },
    {
        title: "Delete",
        value: "delete",
        align: "start",
        width: "5%",
    },
]);

function mapSchemaConfigTaskTemplatesToListItems(taskTemplates: SavedSchemaConfigTaskTemplate[]): SchemaConfigTaskTemplateListItem[] {
    return taskTemplates.map<SchemaConfigTaskTemplateListItem>((schemaConfigTaskTemplate) => {
        const taskGroup = findTaskGroupById(schemaConfigTaskTemplate.taskGroupId, taskGroups);

        if (_.isNil(taskGroup)) {
            return {
                ...schemaConfigTaskTemplate,
                day: -1,
                schemaConfigTaskTemplate,
                taskGroupName: "Error: missing Task Group",
                readoutGroupName: "",
            };
        }

        const readoutGroup = findReadoutGroupById(
            schemaConfigTaskTemplate.readoutGroupId,
            readoutGroups
        );

        const day = _.isNil(schemaConfigTaskTemplate.day)
            ? taskGroup.day
            : schemaConfigTaskTemplate.day;

        if (_.isNil(readoutGroup)) {
            return {
                ...schemaConfigTaskTemplate,
                day,
                schemaConfigTaskTemplate,
                taskGroupName: taskGroup.name,
                readoutGroupName: "Error: missing Readout Group",
            };
        }

        return {
            ...schemaConfigTaskTemplate,
            day,
            schemaConfigTaskTemplate,
            taskGroupName: taskGroup.name,
            readoutGroupName: readoutGroup.name,
        };
    });
}

function showCreateSchemaConfigTaskTemplateForm() {
    showSchemaConfigTaskTemplateFormDialog.value = true;
    selectedSchemaConfigTaskTemplate.value = undefined;
}
function showEditSchemaConfigTaskTemplateForm(item: SchemaConfigTaskTemplateListItem) {
    selectedSchemaConfigTaskTemplate.value = item.schemaConfigTaskTemplate;
    showSchemaConfigTaskTemplateFormDialog.value = true;
}
function schemaConfigTaskTemplateFormDialogClosed() {
    showSchemaConfigTaskTemplateFormDialog.value = false;
    selectedSchemaConfigTaskTemplate.value = undefined;
}
function savedSchemaConfigTaskTemplate() {
    selectedSchemaConfigTaskTemplate.value = undefined;
    showSchemaConfigTaskTemplateFormDialog.value = false;
    emit("saved");
}
async function deleteSchemaConfigTaskTemplate(item: SavedSchemaConfigTaskTemplate) {
    if (confirm("Are you sure you want to delete this task?")) {
        return withLoadingScreen(async () => {
            await taskTemplatesAPI.bySchemaConfig(schemaConfig.id).delete(item.id);
            emit("deleted");
        });
    }
}

</script>
